/**
 * Accordion Component - shadcn-inspired accessible accordion
 * Implements WCAG 2.1 AA compliant accordion with keyboard navigation
 *
 * Usage:
 * <div data-accordion>
 *   <div data-accordion-item class="border-b border-border">
 *     <button data-accordion-trigger class="flex w-full items-center justify-between py-4 text-left font-medium transition-all hover:underline [&[aria-expanded=true]>svg]:rotate-180">
 *       Question text
 *       <svg class="h-4 w-4 shrink-0 transition-transform duration-200">...</svg>
 *     </button>
 *     <div data-accordion-content class="hidden overflow-hidden text-sm text-muted-foreground pb-4 pt-0">
 *       Answer content
 *     </div>
 *   </div>
 * </div>
 */

export class Accordion {
  constructor(element) {
    this.element = element;
    this.items = Array.from(element.querySelectorAll('[data-accordion-item]'));
    this.init();
  }

  init() {
    this.items.forEach((item, index) => {
      const trigger = item.querySelector('[data-accordion-trigger]');
      const content = item.querySelector('[data-accordion-content]');

      if (!trigger || !content) {
        console.warn('Accordion item missing trigger or content:', item);
        return;
      }

      // ARIA attributes for accessibility
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', `accordion-content-${index}`);
      trigger.setAttribute('id', `accordion-trigger-${index}`);

      content.setAttribute('id', `accordion-content-${index}`);
      content.setAttribute('role', 'region');
      content.setAttribute('aria-labelledby', `accordion-trigger-${index}`);

      // Click handler
      trigger.addEventListener('click', () => this.toggle(item));

      // Keyboard navigation
      trigger.addEventListener('keydown', (e) => this.handleKeyboard(e, index));
    });
  }

  toggle(item) {
    const trigger = item.querySelector('[data-accordion-trigger]');
    const content = item.querySelector('[data-accordion-content]');
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';

    // Close all other items (single-open behavior)
    this.items.forEach(otherItem => {
      if (otherItem !== item) {
        const otherTrigger = otherItem.querySelector('[data-accordion-trigger]');
        const otherContent = otherItem.querySelector('[data-accordion-content]');

        if (otherTrigger && otherContent) {
          otherTrigger.setAttribute('aria-expanded', 'false');
          otherContent.classList.add('hidden');
          otherContent.classList.remove('animate-accordion-down');
        }
      }
    });

    // Toggle current item
    if (isOpen) {
      trigger.setAttribute('aria-expanded', 'false');
      content.classList.add('hidden');
      content.classList.remove('animate-accordion-down');
    } else {
      trigger.setAttribute('aria-expanded', 'true');
      content.classList.remove('hidden');
      content.classList.add('animate-accordion-down');
    }
  }

  handleKeyboard(e, index) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIndex = (index + 1) % this.items.length;
      const nextTrigger = this.items[nextIndex].querySelector('[data-accordion-trigger]');
      if (nextTrigger) nextTrigger.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = (index - 1 + this.items.length) % this.items.length;
      const prevTrigger = this.items[prevIndex].querySelector('[data-accordion-trigger]');
      if (prevTrigger) prevTrigger.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      const firstTrigger = this.items[0].querySelector('[data-accordion-trigger]');
      if (firstTrigger) firstTrigger.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      const lastTrigger = this.items[this.items.length - 1].querySelector('[data-accordion-trigger]');
      if (lastTrigger) lastTrigger.focus();
    }
  }
}

// Auto-initialize accordions on DOM load
if (typeof document !== 'undefined') {
  const initAccordions = () => {
    const accordions = document.querySelectorAll('[data-accordion]');
    accordions.forEach(el => new Accordion(el));
  };

  // Check if DOM is already loaded (handles ES6 module deferred loading)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAccordions);
  } else {
    initAccordions();
  }
}
